/**
 * Shared form styles for the ScheduledTasks area — Design System v3 "Signal"
 * §4.4 (Forms) + §4.6 (Feedback) as inline-style objects.
 *
 * Focus styling comes from the global saffron focus ring in index.css —
 * do NOT add local :focus / box-shadow overrides.
 */

import React from 'react';

/** Field: white bg, 1px --border-2, --radius-md, 10–12px padding, 14px Inter. */
const field: React.CSSProperties = {
  padding: '10px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-2)',
  fontSize: 14,
  fontFamily: 'var(--font-sans)',
  backgroundColor: 'var(--cg-neutral-0)',
  color: 'var(--fg-1)',
};

const textarea: React.CSSProperties = {
  ...field,
  minHeight: 100,
  resize: 'vertical',
  lineHeight: 1.5,
};

/** Label: 13px/600 --fg-2. */
const label: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--fg-2)',
};

/** Micro-label: 11px/700/0.08em uppercase --fg-4 (§2.2). */
const microLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 'var(--track-eyebrow)',
  textTransform: 'uppercase',
  color: 'var(--fg-4)',
};

/** Hint: 12.5px --fg-3. */
const hint: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--fg-3)',
  lineHeight: 1.45,
  margin: 0,
};

/** Error text below a field: 12.5px --danger. */
const errorText: React.CSSProperties = {
  fontSize: 12.5,
  color: 'var(--danger)',
  margin: 0,
};

/** Label + field + hint stack with the §4.4 6px gap. */
const group: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

/** Native checkbox with accent fill (§4.4). */
const checkbox: React.CSSProperties = {
  width: 16,
  height: 16,
  accentColor: 'var(--accent)',
  cursor: 'pointer',
  flexShrink: 0,
};

/** Persistent inline error banner (§4.6). */
const errorBanner: React.CSSProperties = {
  background: 'var(--danger-soft)',
  border: '1px solid var(--cg-terra-200)',
  color: 'var(--danger)',
  fontSize: 13,
  fontWeight: 500,
  borderRadius: 'var(--radius-md)',
  padding: '10px 14px',
};

/** Neutral settings-section card wrapping a config sub-form. */
const sectionCard: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.25rem',
  padding: '1rem',
  backgroundColor: 'var(--cg-neutral-25)',
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border-1)',
};

export const formFieldStyles = {
  field,
  textarea,
  label,
  microLabel,
  hint,
  errorText,
  group,
  checkbox,
  errorBanner,
  sectionCard,
};

export default formFieldStyles;
